/**
 * Onion skinning: faint ghost skeletons at the neighbouring KEYED frames.
 *
 * What the animator is actually asking is "where was this pose a key ago, and where is it going
 * next" — so the ghosts sit on the previous and next *keyed* frames, not on `frame ± n`. On the
 * first key there is no cool ghost, on the last there is no warm one, and a clip with no keys at
 * all draws nothing.
 *
 * Ghosts are line skeletons only: no joint dots, nothing pickable, nothing in the export path.
 * A ghost the user can click is a bone selection they did not mean to make.
 *
 * ## The one delicate part
 *
 * Computing a ghost means *posing the real rig* at another frame and reading its world matrices —
 * there is no second skeleton to pose. `update()` therefore runs immediately after
 * `poseDriver.apply()` and before every other reader in the frame (`mount.ts` frame loop), and it
 * MUST put the rig back exactly where the pose driver left it: the IK handles, the bone gizmo and
 * the skeleton overlay all read world positions later in the same frame, and a rig left one
 * evaluation off would park the gizmo on a ghost.
 *
 * The restore is `applyPose(state.livePose, state.liveHipsY)` + `updateMatrixWorld(true)` —
 * literally the two calls the pose driver made, and `applyPose` is a pure function of its two
 * arguments, so the result is bit-identical rather than merely close.
 *
 * Because that round trip is not free, it happens on a cache MISS only: the ghost positions
 * depend on the clip, the rig and the two ghost frame numbers, and none of those change while the
 * playhead moves inside one key interval — which is exactly what scrubbing does.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
} from 'three';

import { evaluateHipsY, evaluatePose } from '../../shared';
import type { CanonicalAnimationClip } from '../../shared';
import type { EditorContext } from '../context';
import { allKeyFrames, nextKeyFrame, prevKeyFrame } from '../timeline/frames';
import { overlayFromRig } from './overlayData';

/*
 * Ghost colours (the `skeletonLayer.ts` COLOR_* convention: the token each one approximates).
 *
 * Both are deliberately desaturated and mid-dark — around the lightness of `--edge-2` (#4a4a4a) —
 * so they read as "not the thing you are editing". Orange stays the viewport's selection colour
 * (design rule 2) and nothing here comes near it.
 */
/** Past key — a cool step toward `--blue` (#4772b3), drained of its UI-state saturation. */
const COLOR_GHOST_PREV = 0x5a7a9a;
/** Next key — the warm mirror of the same step, kept well short of `--orange` (#ed8a3c). */
const COLOR_GHOST_NEXT = 0x9a7a5a;
/** Faint enough to sit behind the live skeleton, solid enough to follow a limb through it. */
const GHOST_OPACITY = 0.5;

/** Which two keyed frames a ghost pair sits on. `null` where there is no key on that side. */
export interface GhostFrames {
  prev: number | null;
  next: number | null;
}

export const NO_GHOSTS: GhostFrames = { prev: null, next: null };

/**
 * The keyed frames on either side of the playhead. `frame` may be fractional (playback), and
 * a playhead sitting exactly ON a key still yields that key's neighbours — the ghost of the pose
 * you are looking at would be invisible anyway.
 */
export function selectGhostFrames(
  clip: CanonicalAnimationClip | null,
  frame: number,
): GhostFrames {
  if (!clip) return NO_GHOSTS;
  const frames = allKeyFrames(clip);
  if (frames.length === 0) return NO_GHOSTS;
  return { prev: prevKeyFrame(frames, frame), next: nextKeyFrame(frames, frame) };
}

/**
 * Identity of everything the ghost joint positions depend on that can be reduced to a string.
 *
 * The clip and the rig are compared by object reference alongside this (`update()`), because a
 * clip edit produces a NEW clip object even when `updatedAt` has not moved — `updatedAt` is here
 * for the opposite case, an in-place library refresh that reuses the object.
 */
export function onionCacheKey(clipUpdatedAt: number, ghosts: GhostFrames): string {
  return `${clipUpdatedAt}|${ghosts.prev ?? 'x'}|${ghosts.next ?? 'x'}`;
}

export interface OnionLayer {
  /**
   * Refresh the ghosts. MUST be called immediately after `poseDriver.apply()` and before any
   * other layer reads world matrices.
   */
  update(): void;
  /** Viewport display switch ("Onion" in the overlay bar). Does not touch editor state. */
  setVisible(visible: boolean): void;
  dispose(): void;
}

export function createOnionLayer(overlayGroup: Group, ctx: EditorContext): OnionLayer {
  const group = new Group();
  group.name = 'asx-onion';
  group.userData.editorOnly = true;
  group.visible = false;
  overlayGroup.add(group);

  interface Ghost {
    lines: LineSegments;
    geometry: BufferGeometry;
    material: LineBasicMaterial;
    positions: Float32Array;
  }

  function createGhost(color: number): Ghost {
    const geometry = new BufferGeometry();
    const material = new LineBasicMaterial({
      color,
      depthTest: false,
      transparent: true,
      opacity: GHOST_OPACITY,
    });
    const lines = new LineSegments(geometry, material);
    // Below the skeleton overlay's own links (998) so the live pose stays on top.
    lines.renderOrder = 997;
    lines.frustumCulled = false;
    lines.visible = false;
    lines.userData.editorOnly = true;
    group.add(lines);
    return { lines, geometry, material, positions: new Float32Array(0) };
  }

  const ghosts = { prev: createGhost(COLOR_GHOST_PREV), next: createGhost(COLOR_GHOST_NEXT) };

  /** Display switch from the viewport bar. */
  let shown = false;

  // --- cache --------------------------------------------------------------

  let cacheKey: string | null = null;
  let cachedClip: CanonicalAnimationClip | null = null;
  let cachedRig: unknown = null;
  let lastMode: string | null = null;

  function invalidate(): void {
    cacheKey = null;
    cachedClip = null;
    cachedRig = null;
  }

  /** Write one ghost's link segments from the rig as it currently stands. */
  function captureInto(ghost: Ghost): void {
    const rig = ctx.session.rig;
    const links = rig ? overlayFromRig(rig).links : [];
    const needed = links.length * 6;
    if (ghost.positions.length !== needed) {
      ghost.positions = new Float32Array(needed);
      ghost.geometry.setAttribute('position', new BufferAttribute(ghost.positions, 3));
    }
    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const o = i * 6;
      ghost.positions[o] = link.from[0];
      ghost.positions[o + 1] = link.from[1];
      ghost.positions[o + 2] = link.from[2];
      ghost.positions[o + 3] = link.to[0];
      ghost.positions[o + 4] = link.to[1];
      ghost.positions[o + 5] = link.to[2];
    }
    const attr = ghost.geometry.getAttribute('position');
    if (attr) attr.needsUpdate = true;
    ghost.geometry.computeBoundingSphere();
    ghost.lines.visible = links.length > 0;
  }

  function hide(): void {
    group.visible = false;
    ghosts.prev.lines.visible = false;
    ghosts.next.lines.visible = false;
  }

  function update(): void {
    const state = ctx.store.getState();
    const mode = state.mode;
    if (mode !== lastMode) {
      lastMode = mode;
      invalidate();
    }

    const rig = ctx.session.rig;
    const clip = state.clip;
    // ANIMATE only. Outside it the skeleton is the rest pose the user is editing, and a ghost of
    // an animation frame on top of it would be actively misleading.
    if (!shown || mode !== 'animate' || !rig || !clip) {
      hide();
      return;
    }

    const frames = selectGhostFrames(clip, state.playhead.frame);
    if (frames.prev === null && frames.next === null) {
      hide();
      return;
    }

    const key = onionCacheKey(clip.updatedAt, frames);
    if (key !== cacheKey || clip !== cachedClip || rig !== cachedRig) {
      cacheKey = key;
      cachedClip = clip;
      cachedRig = rig;

      const root = ctx.session.model?.root ?? null;
      const refresh = (): void => {
        if (root) root.updateMatrixWorld(true);
      };

      if (frames.prev === null) {
        ghosts.prev.lines.visible = false;
      } else {
        rig.applyPose(evaluatePose(clip, frames.prev), evaluateHipsY(clip, frames.prev));
        refresh();
        captureInto(ghosts.prev);
      }
      if (frames.next === null) {
        ghosts.next.lines.visible = false;
      } else {
        rig.applyPose(evaluatePose(clip, frames.next), evaluateHipsY(clip, frames.next));
        refresh();
        captureInto(ghosts.next);
      }

      // Put the frame back exactly as `poseDriver.apply()` left it. Everything after this point
      // in the frame loop reads these matrices — including the hips height: a one-argument
      // `applyPose` here would leave the live pelvis at bind, i.e. showing the last ghost's rig
      // rather than the pose driver's.
      rig.applyPose(state.livePose, state.liveHipsY);
      refresh();
    }

    group.visible = true;
  }

  return {
    update,
    setVisible(visible: boolean): void {
      shown = visible;
      if (!visible) hide();
      // The ghosts are stale only in the sense that they were never wrong: the cache survives a
      // toggle, so switching onion skinning back on costs nothing.
    },
    dispose(): void {
      overlayGroup.remove(group);
      for (const ghost of [ghosts.prev, ghosts.next]) {
        ghost.geometry.dispose();
        ghost.material.dispose();
      }
      invalidate();
    },
  };
}
